import { useFormikContext } from 'formik';

import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { Row, Col } from 'components/grid';
import { formatPrice } from 'utils/helpers';
import { SectionHeader } from 'components/section-header';
import { InputField, SelectField, SwitchField } from 'components/fields';
import {
  PayrollSettingChange,
  usePayrollSettingsQuery,
} from 'store/api/company';
import {
  UnderMinimumBoxKind,
  UNDER_MINIMUM_FIELDS,
  UNDER_MINIMUM_METHODS,
  getUnderMinimumMethod,
} from 'utils/under-minimum';

import * as Styles from './styles';
import { FormValues } from './types';

type InputKey =
  | 'tipFeePercent'
  | 'brokenMissingPercent'
  | 'underMinimumThreshold';
type ToggleKey =
  | 'isTipFeeEnabled'
  | 'isUnderMinimumEnabled'
  | 'isBrokenMissingEnabled';

const isEmpty = (value?: string | null) =>
  value === undefined || value === null || value === '' || value === 'null';

const formatMethodValue = (value: number, kind?: UnderMinimumBoxKind) => {
  switch (kind) {
    case 'dollars':
      return formatPrice(value);
    case 'percent':
      return `${value}%`;
    case 'points':
      return `${value} ${value === 1 ? 'point' : 'points'}`;
    default:
      return String(value);
  }
};

const formatChangeValue = (
  setting: string,
  value?: string,
  boxKind?: UnderMinimumBoxKind
) => {
  if (setting === UNDER_MINIMUM_FIELDS.method) {
    return getUnderMinimumMethod(value).label;
  }
  if (setting === UNDER_MINIMUM_FIELDS.methodValue) {
    if (isEmpty(value)) {
      return 'none';
    }
    return formatMethodValue(Number(value), boxKind);
  }
  return setting.endsWith('Threshold')
    ? formatPrice(Number(value))
    : `${Number(value)}%`;
};

const describeChange = (
  change?: PayrollSettingChange,
  boxKind?: UnderMinimumBoxKind
) => {
  if (!change) {
    return undefined;
  }
  const when = formatDate(change.createdAt, 'MMM D, YYYY h:mm A');
  if (change.setting.startsWith('is')) {
    return `Last changed ${when}: turned ${change.newValue === 'true' ? 'on' : 'off'}`;
  }
  return `Last changed ${when}: ${formatChangeValue(change.setting, change.oldValue, boxKind)} → ${formatChangeValue(change.setting, change.newValue, boxKind)}`;
};

const settings: {
  help: string;
  title: string;
  toggle: ToggleKey;
  input: { label: string; name: InputKey };
}[] = [
  {
    title: 'Tip fee',
    toggle: 'isTipFeeEnabled',
    input: { label: 'Tip fee %', name: 'tipFeePercent' },
    help: 'Reps are paid their tip share minus this %. They see the reduced tip only.',
  },
  {
    toggle: 'isBrokenMissingEnabled',
    title: 'Broken & missing products',
    input: { label: 'Broken & missing %', name: 'brokenMissingPercent' },
    help: 'Each payout gets a "Broken & missing products" deduction: this % of the rep\'s net sales for the event (gross ÷ (1 + tax + card fee), tips excluded).',
  },
  {
    // Owner, 22 Oct 2026: third payroll rule. Values are kept on each sale
    // when it is recorded, so a change applies to later sales only.
    title: 'Under Minimum',
    toggle: UNDER_MINIMUM_FIELDS.toggle,
    help: 'When ON, this applies to every new sale below the cart minimum. Changes apply to later sales only.',
    input: {
      name: UNDER_MINIMUM_FIELDS.threshold,
      label: 'Under Minimum: ignore gaps under ($)',
    },
  },
];

const methodOptions = UNDER_MINIMUM_METHODS.map(({ label, value }) => ({
  label,
  value,
}));

/**
 * Owner, 22 Oct 2026: "How much is taken" for Under Minimum. A dropdown, and
 * a number box only for the methods that need one (% or $).
 */
const UnderMinimumMethodFields = () => {
  const { values } = useFormikContext<FormValues>();
  const method = getUnderMinimumMethod(values.underMinimumMethod);

  return (
    <>
      <Styles.InlineSetting>
        <Styles.InlineInput>
          <SelectField
            onlyValue
            isSearchable={false}
            options={methodOptions}
            label="How much is taken"
            inputId="under-minimum-method"
            name={UNDER_MINIMUM_FIELDS.method}
          />
        </Styles.InlineInput>
        {/* Keeps a stacked field as wide as the first one. */}
        <Styles.InlineToggle aria-hidden />
      </Styles.InlineSetting>
      {method.box && (
        <Styles.InlineSetting>
          <Styles.InlineInput>
            <InputField
              number
              decimal
              label={method.box.label}
              placeholder={method.box.label}
              name={UNDER_MINIMUM_FIELDS.methodValue}
            />
          </Styles.InlineInput>
          <Styles.InlineToggle aria-hidden />
        </Styles.InlineSetting>
      )}
      <Styles.SwitchSubtitle data-testid="underMinimumMethod-help">
        {method.help}
      </Styles.SwitchSubtitle>
    </>
  );
};

/**
 * Owner, 17 Oct 2026: two payroll settings. They apply to unpaid payouts
 * and everything after; paid payouts never change. Owner, 22 Oct 2026: a
 * third, Under Minimum, which applies to sales after a change only.
 */
export const PayrollSettingsSection = ({
  companyId,
}: {
  companyId?: number;
}) => {
  const { data } = usePayrollSettingsQuery(String(companyId), {
    skip: !companyId,
  });

  const { values } = useFormikContext<FormValues>();

  const lastChange = (keys: string[]) =>
    data?.history?.find((change) => keys.includes(change.setting));

  return (
    <>
      <SectionHeader
        title="Payroll"
        withBorder={false}
        subTitle="Applies to payouts not paid yet and to everything after. Paid payouts never change."
      />
      {settings.map(({ help, title, input, toggle }) => {
        const isUnderMinimum = toggle === UNDER_MINIMUM_FIELDS.toggle;
        const change = describeChange(
          lastChange(
            isUnderMinimum
              ? [
                  toggle,
                  input.name,
                  UNDER_MINIMUM_FIELDS.method,
                  UNDER_MINIMUM_FIELDS.methodValue,
                ]
              : [toggle, input.name]
          ),
          isUnderMinimum
            ? getUnderMinimumMethod(values.underMinimumMethod).box?.kind
            : undefined
        );
        return (
          <Row key={toggle}>
            <Col col={4}>
              <Flex gap={8} flexDirection="column">
                {/* Owner 17 Oct: one row per setting, "input | toggle". */}
                <Styles.InlineSetting>
                  <Styles.InlineInput>
                    <InputField
                      number
                      decimal
                      name={input.name}
                      label={input.label}
                      placeholder={input.label}
                    />
                  </Styles.InlineInput>
                  <Styles.InlineToggle data-testid={`${toggle}-toggle`}>
                    <SwitchField name={toggle} aria-label={title} />
                  </Styles.InlineToggle>
                </Styles.InlineSetting>
                {isUnderMinimum && <UnderMinimumMethodFields />}
                {change && (
                  <Styles.SwitchSubtitle data-testid={`${toggle}-last-change`}>
                    {change}
                  </Styles.SwitchSubtitle>
                )}
                <Styles.SwitchSubtitle>{help}</Styles.SwitchSubtitle>
              </Flex>
            </Col>
          </Row>
        );
      })}
    </>
  );
};
